<script setup lang="ts">
import {DetectionCandidate, DetectionErrorProps} from "@/notification/interfaces";
import dateHelper from "@/core/helpers/dateHelper";

defineProps<{
    error: DetectionErrorProps;
}>();

defineEmits<{
    pick: [candidate: DetectionCandidate];
}>();

/**
 * Format a name with a leading capital (e.g. "JEAN" → "Jean").
 */
const ucfirst = (name?: string | null): string => {
    if (!name) {
        return '';
    }

    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

/**
 * The candidate's own name.
 */
const candidateName = (candidate: DetectionCandidate): string => {
    return `${ucfirst(candidate.first_name)} ${ucfirst(candidate.last_name)}`.trim();
};

/**
 * Tell the candidate apart from its homonyms: the insured party it belongs
 * to, and its birth date when known.
 */
const candidateDetails = (candidate: DetectionCandidate): string => {
    const relation = candidate.type === 'beneficiary'
        ? `Ayant droit de ${ucfirst(candidate.customer.first_name)} ${ucfirst(candidate.customer.last_name)}`.trim()
        : 'Assuré(e)';

    return candidate.birth_date
        ? `${relation} · né(e) le ${dateHelper.shortFormat(candidate.birth_date)}`
        : relation;
};

</script>

<template>
  <div class="rounded-lg border border-orange-200 text-sm bg-orange-50 p-3">
    <div class="flex flex-col gap-2">
      <div class="flex flex-row items-center gap-2">
        <p class="text-lg">
          👥
        </p>
        <p class="text font-semibold">
          Plusieurs patients possibles
        </p>
      </div>
      <p class="text leading-relaxed">
        Sélectionnez le bon patient :
      </p>
      <div class="flex flex-col gap-2">
        <button
          v-for="candidate in error.candidates"
          :key="candidate.consumer_key"
          class="rounded-lg border border-gray-200 bg-white p-2 text-left cursor-pointer hover:border-primary/50"
          @click="$emit('pick', candidate)"
        >
          <p class="font-semibold">
            {{ candidateName(candidate) }}
          </p>
          <p class="text-xs text-gray-600">
            {{ candidateDetails(candidate) }}
          </p>
        </button>
      </div>
    </div>
  </div>
</template>
